import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight, CircleAlert, Plus, Search } from 'lucide-react'
import { listSales, scanDrafts, voidEmptyDrafts, postValidDrafts } from '../../api/sales'
import ErrorBanner from '../../components/ErrorBanner'
import { useToast } from '../../components/Toast.jsx'
import Button from '../../components/Button'
import Modal from '../../components/Modal'
import Spinner from '../../components/Spinner'
import { FilterChips, StatusBadge } from '../../components/PageParts'
import { fmtDateTime, fmtDay } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import SalesTabs from './SalesTabs'
import { PERIODS, money, num, periodFor, periodRange, resumeRefundPath, saleNo, statusOf } from './salesShared'

const PAGE_SIZE = 25

const STATUSES = [
  { id: 'all', label: 'All', match: () => true },
  { id: 'posted', label: 'Completed', match: (s) => s === 'posted' },
  { id: 'refunded', label: 'Refunds', match: (s) => s === 'refunded' },
  { id: 'draft', label: 'Unfinished', match: (s) => s === 'draft' },
  { id: 'voided', label: 'Voided', match: (s) => s === 'voided' || s === 'void' },
]

const STATUS_LABEL = { posted: 'Completed', refunded: 'Refund', draft: 'Unfinished', voided: 'Voided', void: 'Voided' }

export default function SaleList() {
  const nav = useNavigate()
  const { notify } = useToast()
  const [searchParams, setSearchParams] = useSearchParams()

  const initial = (() => {
    const s = searchParams.get('start')
    const e = searchParams.get('end')
    if (!s || !e) return { period: 'today', range: periodRange('today') }
    return { period: periodFor(s, e), range: [s, e] }
  })()
  const [period, setPeriod] = useState(initial.period)
  const [[start, end], setRange] = useState(initial.range)
  const [status, setStatus] = useState(searchParams.get('status') || 'all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [errMsg, setErrMsg] = useState('')
  const [drafts, setDrafts] = useState(null)
  const [cleanupOpen, setCleanupOpen] = useState(false)
  const [cleaning, setCleaning] = useState('')

  const load = async (from = start, to = end) => {
    if (from && to && from > to) { setErrMsg('The start date must be on or before the end date.'); return }
    setErrMsg('')
    setLoading(true)
    try {
      const [data, scan] = await Promise.all([
        listSales({ start: from, end: to }),
        scanDrafts({ start: from, end: to }).catch(() => null),
      ])
      setRows(Array.isArray(data) ? data : [])
      setDrafts(scan)
    } catch (e) {
      setRows([])
      setErrMsg(e.message || 'Could not load sales.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load(start, end)
    const next = new URLSearchParams()
    next.set('start', start)
    next.set('end', end)
    if (status !== 'all') next.set('status', status)
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end])

  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (status === 'all') next.delete('status')
      else next.set('status', status)
      return next
    }, { replace: true })
    setPage(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  useEffect(() => { setPage(1) }, [search, start, end])

  const choosePeriod = (id) => {
    setPeriod(id)
    if (id !== 'custom') setRange(periodRange(id))
  }

  const counts = useMemo(() => Object.fromEntries(
    STATUSES.map((s) => [s.id, rows.filter((r) => s.match(statusOf(r))).length])
  ), [rows])

  const filtered = useMemo(() => {
    const match = STATUSES.find((s) => s.id === status)?.match || (() => true)
    const term = search.trim().toLowerCase()
    return rows.filter((r) => match(statusOf(r)) && (!term || [
      saleNo(r), r.location_name, r.payment_method,
    ].join(' ').toLowerCase().includes(term)))
  }, [rows, status, search])

  const completedTotal = filtered.filter((r) => statusOf(r) === 'posted').reduce((sum, r) => sum + num(r.total_amount ?? r.revenue), 0)
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageSafe = Math.min(page, pageCount)
  const pageRows = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE)
  const unfinished = num(drafts?.empty_count) + num(drafts?.postable_count) + num(drafts?.refund_count)

  const cleanup = async (kind) => {
    setCleaning(kind)
    try {
      if (kind === 'void') {
        await voidEmptyDrafts({ start, end })
        notify('Empty drafts voided')
      } else {
        const r = await postValidDrafts({ start, end })
        const n = num(r?.posted)
        const failed = num(r?.failed)
        const why = r?.errors?.[0]?.error ? ` (${r.errors[0].error})` : ''
        notify(failed ? `${n} completed, ${failed} left as drafts${why}` : `${n} ${n === 1 ? 'sale' : 'sales'} completed`)
      }
      await load()
    } catch (e) {
      notify(e.message || 'That did not work. Please try again.')
    } finally {
      setCleaning('')
    }
  }

  return (
    <div className="ci-page">
      <SalesTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Sales</h1>
            <div className="ci-title-meta">Every sale made in store, and every refund.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <Link className="ci-btn ci-btn-primary" to="/sales/pos"><Plus size={15} />New sale</Link>
        </div>
      </div>

      <div className="ci-toolbar">
        <label className="ci-search" aria-label="Search sales">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search sales…" />
        </label>
        <select className="ci-select" value={period} onChange={(e) => choosePeriod(e.target.value)} aria-label="Period">
          {PERIODS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        {period === 'custom' && (
          <span className="list-dates">
            <input type="date" className="ci-select" value={start} onChange={(e) => setRange([e.target.value, end])} aria-label="From" />
            <span>to</span>
            <input type="date" className="ci-select" value={end} onChange={(e) => setRange([start, e.target.value])} aria-label="To" />
          </span>
        )}
        <div className="ci-toolbar-right">
          {unfinished > 0 && (
            <button type="button" className="section-chip" onClick={() => setCleanupOpen(true)}>
              <CircleAlert size={14} aria-hidden="true" />
              {unfinished} unfinished
            </button>
          )}
        </div>
      </div>

      <FilterChips options={STATUSES.map((s) => ({ id: s.id, label: s.label, count: counts[s.id] }))} value={status} onChange={setStatus} />

      {errMsg && <ErrorBanner message={errMsg} />}

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table">
            <thead>
              <tr>
                <th>Sale</th>
                <th>Date and time</th>
                <th>Location</th>
                <th>Paid by</th>
                <th className="num">Items</th>
                <th className="num">Total</th>
                <th className="center">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7"><Spinner label="Loading sales…" /></td></tr>
              ) : pageRows.length === 0 ? (
                <tr><td colSpan="7"><div className="ci-empty">No sales {period === 'today' ? 'yet today' : 'in this period'}.</div></td></tr>
              ) : pageRows.map((r) => {
                const s = statusOf(r)
                return (
                  <tr key={r.sale_id} className="row-link" onClick={() => nav(`/sales/detail/${r.sale_id}`)}>
                    <td><Link className="ci-je-link" to={`/sales/detail/${r.sale_id}`} onClick={(e) => e.stopPropagation()}>{saleNo(r)}</Link></td>
                    <td className="ci-muted-cell">{fmtDateTime(r.sale_datetime)}</td>
                    <td>{r.location_name || '–'}</td>
                    <td>{r.payment_method || '–'}</td>
                    <td className="num">{num(r.quantity).toLocaleString()}</td>
                    <td className={`num ci-total${s === 'refunded' ? ' text-bad' : ''}`}>{s === 'refunded' ? '−' : ''}{money(Math.abs(num(r.total_amount ?? r.revenue)))}</td>
                    <td className="center"><StatusBadge status={s} label={STATUS_LABEL[s] || r.status} /></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="ci-pagination">
          <span className="ci-page-info">
            {filtered.length
              ? `${filtered.length} ${filtered.length === 1 ? 'sale' : 'sales'} · completed sales ${money(completedTotal)}`
              : 'No results'}
          </span>
          {pageCount > 1 && (
            <div className="ci-page-buttons">
              <button type="button" className="ci-page-btn" disabled={pageSafe === 1} onClick={() => setPage(pageSafe - 1)}><ChevronLeft size={13} />Previous</button>
              <span className="ci-page-info">Page {pageSafe} of {pageCount}</span>
              <button type="button" className="ci-page-btn" disabled={pageSafe === pageCount} onClick={() => setPage(pageSafe + 1)}>Next<ChevronRight size={13} /></button>
            </div>
          )}
        </div>
      </div>

      <Modal
        open={cleanupOpen}
        onClose={() => setCleanupOpen(false)}
        title="Unfinished sales"
        subtitle={start === end ? fmtDay(start) : `${fmtDay(start)} to ${fmtDay(end)}`}
        size="lg"
        footer={<Button variant="ghost" onClick={() => setCleanupOpen(false)}>Close</Button>}
      >
        <ul className="cleanup-list">
          <li>
            <div>
              <strong>Empty sales</strong>
              <span>Started on the New sale screen, but nothing was added. Safe to void.</span>
            </div>
            <span className="cleanup-count">{num(drafts?.empty_count)}</span>
            <Button variant="outline" size="sm" disabled={!num(drafts?.empty_count)} loading={cleaning === 'void'} onClick={() => cleanup('void')}>Void them</Button>
          </li>
          <li>
            <div>
              <strong>Sales with items</strong>
              <span>Rung up but never completed. Completing them records the sale.</span>
            </div>
            <span className="cleanup-count">{num(drafts?.postable_count)}</span>
            <Button variant="primary" size="sm" disabled={!num(drafts?.postable_count)} loading={cleaning === 'post'} onClick={() => cleanup('post')}>Complete them</Button>
          </li>
          <li>
            <div>
              <strong>Refunds</strong>
              <span>Started but not finished. Open each one to finish or cancel it.</span>
            </div>
            <span className="cleanup-count">{num(drafts?.refund_count)}</span>
            <span />
          </li>
        </ul>
        {(drafts?.refund_rows || []).length > 0 && (
          <table className="ci-line-table">
            <thead>
              <tr><th>Refund</th><th>Started</th><th>For sale</th><th className="num">Items</th><th /></tr>
            </thead>
            <tbody>
              {drafts.refund_rows.map((row) => {
                const original = row.original_sale_id
                const resume = resumeRefundPath(row)
                return (
                  <tr key={row.refund_sale_id || row.sale_id}>
                    <td>{saleNo(row)}</td>
                    <td>{fmtDateTime(row.sale_datetime)}</td>
                    <td>{original ? docNo('sale', row.original_sale_no) : '–'}</td>
                    <td className="num">{row.line_count ?? '–'}</td>
                    <td className="num">
                      {resume && (
                        <Button variant="outline" size="sm" onClick={() => { setCleanupOpen(false); nav(resume) }}>Continue</Button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </Modal>
    </div>
  )
}
